import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { ArrowPathIcon, ShieldCheckIcon, BuildingOffice2Icon, CheckCircleIcon, ExclamationTriangleIcon, NoSymbolIcon, ClockIcon } from '@heroicons/react/24/outline';
import { apiGet, apiPost } from '../../../utils/api';

interface MarketplaceMembership {
  membershipId: number;
  role: string;
  organisationType: 'buyer' | 'supplier' | 'both';
  capabilities: string[];
  tenantId: number;
}

interface MarketplaceAccessStatus {
  tenantAccessStatus: string;
  hasMembership: boolean;
  membershipStatus: string | null;
  role: string | null;
  organisationType: 'buyer' | 'supplier' | 'both' | null;
  capabilities: string[];
  hasProfile: boolean;
  profileStatus: string | null;
  verificationReason: string | null;
}

interface MarketplaceRouteGuardProps {
  children: React.ReactNode;
  requiredOrganisationType?: 'buyer' | 'supplier';
}

// ─── Small reusable state screens ────────────────────────────────────────────

interface StateScreenProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: { label: string; onClick: () => void };
}

const StateScreen: React.FC<StateScreenProps> = ({ icon, title, description, action }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        {icon}
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">{title}</h2>
      <p className="text-gray-600 text-sm mb-6">{description}</p>
      {action && (
        <button onClick={action.onClick}
          className="px-5 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition">
          {action.label}
        </button>
      )}
    </div>
  </div>
);

const SupplierRegistrationForm: React.FC<{ onSuccess: () => void; onCancel: () => void }> = ({ onSuccess, onCancel }) => {
  const [form, setForm] = useState({
    business_name: '', business_type: 'agricultural_supplier',
    contact_email: '', contact_phone: '', contact_person: '',
    address: '', city: '', country: 'Rwanda', tax_id: '', logo_url: '',
  });
  const [products, setProducts] = useState<string[]>(['']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const addProduct = () => setProducts([...products, '']);
  const removeProduct = (i: number) => setProducts(products.filter((_, idx) => idx !== i));
  const updateProduct = (i: number, val: string) => {
    const next = [...products];
    next[i] = val;
    setProducts(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.business_name.trim()) { setError('Business name is required'); return; }
    const validProducts = products.filter(p => p.trim());
    if (validProducts.length === 0) { setError('Add at least one product'); return; }
    setSubmitting(true);
    setError('');
    try {
      await apiPost('/api/marketplace/b2b/register-supplier', {
        ...form,
        products: validProducts,
      });
      setSuccess(true);
      setTimeout(() => onSuccess(), 2000);
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <CheckCircleIcon className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Registration Submitted!</h2>
          <p className="text-gray-600 text-sm">Your supplier registration has been submitted for review. You will be notified once a decision is made.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="rounded-xl bg-amber-100 p-3">
            <ShieldCheckIcon className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Register as Supplier</h2>
            <p className="text-sm text-gray-500">Complete your business profile to join the marketplace</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
              <input type="text" value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm" placeholder="e.g. Green Harvest Ltd" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
              <select value={form.business_type} onChange={e => setForm({ ...form, business_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm">
                <option value="agricultural_supplier">Agricultural Supplier</option>
                <option value="input_dealer">Input Dealer</option>
                <option value="equipment_supplier">Equipment Supplier</option>
                <option value="logistics_provider">Logistics Provider</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID</label>
              <input type="text" value={form.tax_id} onChange={e => setForm({ ...form, tax_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
              <input type="text" value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
              <input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input type="text" value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input type="text" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input type="text" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm" />
            </div>
          </div>

          {/* Products */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Products *</label>
            <p className="text-xs text-gray-500 mb-2">List the products you want to sell on the marketplace</p>
            <div className="space-y-2">
              {products.map((p, i) => (
                <div key={i} className="flex gap-2">
                  <input type="text" value={p} onChange={e => updateProduct(i, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                    placeholder={`Product ${i + 1} (e.g. Fertilizer, Seeds, Equipment)`} />
                  {products.length > 1 && (
                    <button type="button" onClick={() => removeProduct(i)}
                      className="px-2 py-2 text-red-500 hover:text-red-700 text-sm">Remove</button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={addProduct}
              className="mt-2 text-sm text-amber-600 hover:text-amber-700 font-medium">+ Add another product</button>
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="flex items-center gap-2 px-5 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50 transition">
              {submitting ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <ShieldCheckIcon className="w-4 h-4" />}
              {submitting ? 'Submitting...' : 'Submit Registration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * MarketplaceRouteGuard
 *
 * Resolves the exact marketplace access state for the current user and shows
 * the appropriate screen:
 *  - Super Admin bypasses all checks.
 *  - Tenant marketplace disabled/suspended/revoked → disabled screen.
 *  - Active membership → organisation-type check for the route.
 *  - Membership pending/suspended → state screen (never registration).
 *  - Supplier profile pending review → pending screen.
 *  - Supplier profile rejected → rejected screen with re-apply option.
 *  - No membership + no profile:
 *      - supplier entry points → supplier registration form
 *      - buyer entry points → access-required screen (no supplier registration)
 */
const MarketplaceRouteGuard: React.FC<MarketplaceRouteGuardProps> = ({
  children,
  requiredOrganisationType,
}) => {
  const { user } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<MarketplaceAccessStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRegistration, setShowRegistration] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (isSuperAdmin) {
      setLoading(false);
      return;
    }

    const checkMembership = async () => {
      try {
        const data = await apiGet<MarketplaceAccessStatus>('/api/marketplace/b2b/membership-status');
        setStatus(data);
        setLoading(false);
      } catch (err: any) {
        console.error('Marketplace access check failed:', err);
        setError(err.message || 'Failed to verify marketplace access.');
        setLoading(false);
      }
    };

    checkMembership();
  }, [user, isSuperAdmin, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <ArrowPathIcon className="w-8 h-8 text-emerald-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-sm">Checking marketplace access...</p>
        </div>
      </div>
    );
  }

  if (isSuperAdmin) {
    return <>{children}</>;
  }

  if (error) {
    return (
      <StateScreen
        icon={<ExclamationTriangleIcon className="w-8 h-8 text-red-500" />}
        title="Marketplace access check failed"
        description={error}
        action={{ label: 'Back to Dashboard', onClick: () => window.location.href = '/dashboard' }}
      />
    );
  }

  // Re-apply after a rejected profile opens the registration form
  if (showRegistration) {
    return (
      <SupplierRegistrationForm
        onSuccess={() => {
          setShowRegistration(false);
          window.location.reload();
        }}
        onCancel={() => window.location.href = '/dashboard'}
      />
    );
  }

  if (!status) {
    return <Navigate to="/dashboard" replace />;
  }

  // 1. Tenant-level entitlement
  if (status.tenantAccessStatus !== 'enabled') {
    const descriptions: Record<string, string> = {
      not_enabled: 'Marketplace access is not enabled for your organisation. Contact your administrator.',
      pending_review: 'Your organisation marketplace access is under review. You will be notified once it is enabled.',
      suspended: 'Marketplace access has been suspended for your organisation. Contact support for more information.',
      revoked: 'Marketplace access has been revoked for your organisation. Contact support for more information.',
    };
    return (
      <StateScreen
        icon={<NoSymbolIcon className="w-8 h-8 text-gray-500" />}
        title="Marketplace not available"
        description={descriptions[status.tenantAccessStatus] || descriptions.not_enabled}
        action={{ label: 'Back to Dashboard', onClick: () => window.location.href = '/dashboard' }}
      />
    );
  }

  // 2. Active membership
  if (status.hasMembership && status.membershipStatus === 'active') {
    const membership: MarketplaceMembership = {
      membershipId: 0,
      role: status.role || '',
      organisationType: status.organisationType || 'supplier',
      capabilities: status.capabilities || [],
      tenantId: user?.tenant_id || 0,
    };

    // A "both" membership satisfies buyer and supplier entry points.
    const organisationTypes = membership.organisationType === 'both'
      ? ['buyer', 'supplier']
      : [membership.organisationType];
    if (requiredOrganisationType && !organisationTypes.includes(requiredOrganisationType)) {
      return <Navigate to="/dashboard" replace />;
    }
    return <>{children}</>;
  }

  // 3. Membership exists but is not active
  if (status.membershipStatus && status.membershipStatus !== 'active') {
    const isSuspended = status.membershipStatus === 'suspended';
    return (
      <StateScreen
        icon={isSuspended
          ? <NoSymbolIcon className="w-8 h-8 text-red-500" />
          : <ClockIcon className="w-8 h-8 text-amber-500" />}
        title={isSuspended ? 'Marketplace access suspended' : 'Marketplace access pending'}
        description={isSuspended
          ? 'Your marketplace membership has been suspended. Contact your administrator or eSanza support.'
          : 'Your marketplace membership is pending activation. You will get access once it is approved.'}
        action={{ label: 'Back to Dashboard', onClick: () => window.location.href = '/dashboard' }}
      />
    );
  }

  // 4. No membership — check supplier profile state
  if (status.hasProfile) {
    const profileStatus = status.profileStatus;
    if (profileStatus === 'submitted' || profileStatus === 'under_review') {
      return (
        <StateScreen
          icon={<ClockIcon className="w-8 h-8 text-amber-500" />}
          title="Registration Pending"
          description="Your supplier registration has been submitted and is being reviewed by our team. You will be notified once a decision is made."
          action={{ label: 'Back to Dashboard', onClick: () => window.location.href = '/dashboard' }}
        />
      );
    }
    if (profileStatus === 'rejected') {
      return (
        <StateScreen
          icon={<ExclamationTriangleIcon className="w-8 h-8 text-red-500" />}
          title="Registration Rejected"
          description={status.verificationReason
            ? `Your supplier registration was not approved: ${status.verificationReason}`
            : 'Your supplier registration was not approved. Review the feedback and re-apply.'}
          action={{ label: 'Re-apply', onClick: () => setShowRegistration(true) }}
        />
      );
    }
    if (profileStatus === 'suspended' || profileStatus === 'expired') {
      return (
        <StateScreen
          icon={<NoSymbolIcon className="w-8 h-8 text-red-500" />}
          title="Account suspended"
          description="Your supplier account is currently suspended. Contact eSanza support for more information."
          action={{ label: 'Back to Dashboard', onClick: () => window.location.href = '/dashboard' }}
        />
      );
    }
    // profile exists in 'draft' — treat like no access below
  }

  // 5. No membership, no usable profile
  if (requiredOrganisationType === 'supplier') {
    return (
      <SupplierRegistrationForm
        onSuccess={() => {
          setShowRegistration(false);
          window.location.reload();
        }}
        onCancel={() => window.location.href = '/dashboard'}
      />
    );
  }

  // Buyer entry points must NOT show supplier registration.
  return (
    <StateScreen
      icon={<BuildingOffice2Icon className="w-8 h-8 text-gray-500" />}
      title="No marketplace access yet"
      description="Your organisation does not have buyer marketplace access configured for your account. Contact your administrator to request access."
      action={{ label: 'Back to Dashboard', onClick: () => window.location.href = '/dashboard' }}
    />
  );
};

export default MarketplaceRouteGuard;
